import { Box, Button, Collapse, Stack, Typography } from "@mui/material";
import { useLayoutEffect, useRef, useState } from "react";
import { ReadOnlySDG } from "../../../readOnlyComponents/ReadOnlySDG";
import {
  FieldDisplayProps,
  FieldLabelProps,
  RichTextFieldDisplayProps,
} from "../../types";
import { getDisplayValue } from "../../../../formatting/string-manipulation";
import { placeholder } from "../../../../formatting/defaults";
import { getArrayDisplayValues } from "../../../../formatting/array-manipulation";
import { sanitiseText } from "../../../../formatting/sanitise-rich-text";
import { ReadOnlyReadyForSaleRadio } from "../../../readOnlyComponents/ReadOnlyReadyForSaleRadio";
import DisplayOnlyTextField from "./DisplayOnlyTextField";

export function FieldDisplay({
  field,
  resolvedFieldId,
  highlightMissingFields = true,
}: FieldDisplayProps) {
  switch (field.type) {
    case "text":
      return (
        <TextFieldDisplay
          field={field}
          resolvedFieldId={resolvedFieldId}
          highlightMissingFields={highlightMissingFields}
        />
      );
    case "sdg":
      return (
        <SdgFieldDisplay
          field={field}
          resolvedFieldId={resolvedFieldId}
          highlightMissingFields={highlightMissingFields}
        />
      );
    case "isReadyForSale":
      return (
        <Box sx={{ mb: 3 }}>
          <ReadOnlyReadyForSaleRadio
            isReady={field.value as boolean}
            reason={field.notReadyForSaleReason}
          />
        </Box>
      );
  }
}

export function FieldLabel({ name }: FieldLabelProps) {
  if (!name) return null;

  return (
    <Typography
      variant="caption"
      color="primary.main"
      sx={{ fontSize: "1rem", fontWeight: 900 }}
    >
      {name}
    </Typography>
  );
}

export function MissingField({ field, resolvedFieldId }: FieldDisplayProps) {
  return (
    <DisplayOnlyTextField
      id={resolvedFieldId}
      label={field.name}
      value=""
      error
      helperText="Missing field"
      badge={field.badge}
    />
  );
}

export function TextFieldDisplay({
  field,
  resolvedFieldId,
  highlightMissingFields = true,
}: FieldDisplayProps) {
  const displayValue = Array.isArray(field.value)
    ? getArrayDisplayValues(field.value, "singleLine")
    : getDisplayValue(field.value);

  if (displayValue === placeholder) {
    return (
      <Stack spacing={0.5} sx={{ minWidth: 0 }}>
        {highlightMissingFields ? (
          <MissingField field={field} resolvedFieldId={resolvedFieldId} />
        ) : (
          <DisplayOnlyTextField
            id={resolvedFieldId}
            label={field.name}
            value={placeholder}
            badge={field.badge}
          />
        )}
      </Stack>
    );
  }

  if (field.richText) {
    return (
      <RichTextFieldDisplay
        field={field}
        resolvedFieldId={resolvedFieldId}
        displayValue={displayValue}
      />
    );
  }

  return (
    <Stack spacing={0.5} sx={{ minWidth: 0 }}>
      <DisplayOnlyTextField
        value={displayValue}
        id={resolvedFieldId}
        label={field.name}
        badge={field.badge}
      />
    </Stack>
  );
}

const collapsedHeight = 96;

function RichTextFieldDisplay({
  field,
  resolvedFieldId,
  displayValue,
}: RichTextFieldDisplayProps) {
  const [expanded, setExpanded] = useState(false);
  const [canCollapse, setCanCollapse] = useState(false);
  const contentRef = useRef<HTMLDivElement>(null);
  const contentId = `${resolvedFieldId}-content`;

  useLayoutEffect(() => {
    const contentElement = contentRef.current;

    if (!contentElement) return;
    if (typeof ResizeObserver === "undefined") return;

    const updateCanCollapse = () => {
      setCanCollapse(contentElement.scrollHeight > collapsedHeight);
    };

    updateCanCollapse();

    const resizeObserver = new ResizeObserver(updateCanCollapse);
    resizeObserver.observe(contentElement);

    return () => resizeObserver.disconnect();
  }, [displayValue]);

  const content = (
    <Box
      ref={contentRef}
      id={contentId}
      dangerouslySetInnerHTML={{ __html: sanitiseText(displayValue) }}
    />
  );

  return (
    <Stack spacing={0.5} sx={{ minWidth: 0 }}>
      <Box
        id={resolvedFieldId}
        data-testid={`field-${field.index}-value`}
        role="textbox"
        aria-label={field.name ?? undefined}
        aria-readonly="true"
        sx={{
          position: "relative",
          mt: 1,
          mb: 2,
          px: 1.75,
          py: 1.25,
          minHeight: 40,
          border: 1,
          borderColor: "rgba(0, 0, 0, 0.23)",
          borderRadius: 1,
          overflowWrap: "anywhere",
          "&:hover": {
            borderColor: "text.primary",
          },
          "& a": {
            color: "primary.main",
            textDecoration: "underline",
          },
          "& p:first-of-type": {
            mt: 0,
          },
          "& p:last-of-type": {
            mb: 0,
          },
        }}
      >
        {field.name && (
          <Typography
            component="span"
            sx={{
              position: "absolute",
              top: 0,
              left: 10,
              px: 0.5,
              backgroundColor: "background.paper",
              color: "text.secondary",
              fontSize: "0.75rem",
              lineHeight: 1,
              transform: "translateY(-50%)",
            }}
          >
            {field.name}
          </Typography>
        )}
        {field.collapsible ? (
          <>
            <Collapse in={expanded} collapsedSize={collapsedHeight}>
              {content}
            </Collapse>
            {canCollapse && (
              <Button
                size="small"
                aria-controls={contentId}
                aria-expanded={expanded}
                onClick={() =>
                  setExpanded((currentExpanded) => !currentExpanded)
                }
                sx={{ mt: 0.5, px: 0, minWidth: 0 }}
              >
                {expanded ? "Show less" : "Show more"}
              </Button>
            )}
          </>
        ) : (
          content
        )}
      </Box>
    </Stack>
  );
}

export function SdgFieldDisplay({
  field,
  resolvedFieldId,
  highlightMissingFields = true,
}: FieldDisplayProps) {
  const sdg = Array.isArray(field.value)
    ? field.value.filter(
        (value): value is number =>
          typeof value === "number" && Number.isFinite(value),
      )
    : [];

  return (
    <Stack spacing={0.5} sx={{ minWidth: 0 }}>
      {sdg.length === 0 ? (
        highlightMissingFields ? (
          <MissingField field={field} resolvedFieldId={resolvedFieldId} />
        ) : (
          <DisplayOnlyTextField
            id={resolvedFieldId}
            label={field.name}
            value={placeholder}
          />
        )
      ) : (
        <ReadOnlySDG sdg={sdg} />
      )}
    </Stack>
  );
}
